import {
  PayrollFileColumnTransformation,
  PayrollFileValueTransformation,
} from '@api/lib/payrollProcessing/types';

export const COLUMN_TRANSFORMATION_OPTIONS: {
  type: PayrollFileColumnTransformation['type'];
  label: string;
  description: string;
  example?: string;
}[] = [
  {
    type: 'NONE',
    label: 'Single Column',
    description: 'Use a single column as-is without combining with others.',
  },
  {
    type: 'COLUMN_FALLBACK',
    label: 'Use First Non-Empty Column',
    description: 'Choose the first non-empty value from the provided columns.',
    example: 'if "Home Phone" column blank, use "Work Phone" column',
  },
  {
    type: 'COLUMN_TEXT_CONCATENATION',
    label: 'Combine Text Columns',
    description: 'Join multiple text columns together with spaces',
    example: `"John" + "Doe" = "John Doe"`,
  },
  {
    type: 'COLUMN_ADDITION',
    label: 'Sum Numeric Columns',
    description: 'Sum the values of multiple numeric columns',
    example: `100 + 50 + 25 = 175`,
  },
  {
    type: 'COLUMN_SUBTRACTION',
    label: 'Subtract Numeric Columns',
    description: 'Subtract the values of multiple numeric columns',
    example: `200 - 50 - 25 = 125`,
  },
  {
    type: 'COLUMN_MULTIPLICATION',
    label: 'Multiply Numeric Columns',
    description: 'Multiply the values of multiple numeric columns',
    example: `10 * 5 * 2 = 100`,
  },
  {
    type: 'COLUMN_DIVISION',
    label: 'Divide Numeric Columns',
    description: 'Divide the values of multiple numeric columns',
    example: `100 / 5 / 2 = 10`,
  },
  {
    type: 'COLUMN_AVERAGE',
    label: 'Average Numeric Columns',
    description: 'Calculate the average of multiple numeric columns',
    example: `(100 + 50 + 25) / 3 = 58.33`,
  },
  {
    type: 'COLUMN_MAXIMUM',
    label: 'Maximum of Numeric Columns',
    description: 'Find the highest numeric value among multiple columns',
    example: `max(100, 50, 25) = 100`,
  },
  {
    type: 'COLUMN_MINIMUM',
    label: 'Minimum of Numeric Columns',
    description: 'Find the lowest numeric value among multiple columns',
    example: `min(100, 50, 25) = 25`,
  },
  {
    type: 'COLUMN_FORMULA',
    label: 'Custom Math Formula',
    description:
      'Use a numeric formula combining selected columns using $1, $2... and + - * / with parentheses',
    example: '$1 + $2 - ($3 * $4)',
  },
  {
    type: 'STATIC_VALUE',
    label: 'Static Value',
    description: 'Always use a fixed value for every row, without reading from any file column.',
    example: 'Pay Schedule always set to "BW"',
  },
];

export const VALUE_TRANSFORMATION_OPTIONS: {
  type: PayrollFileValueTransformation['type'];
  label: string;
  description: string;
  example?: string;
}[] = [
  {
    type: 'NONE',
    label: 'No Transformation',
    description: 'Use the value as-is without any changes.',
  },
  {
    type: 'VALUE_MAPPING',
    label: 'Map Specific Values',
    description: 'Maps specific input values to new output values',
    example: `"Y" → "Yes", "N" → "No"`,
  },
  {
    type: 'DEFAULT_IF_EMPTY',
    label: 'Default If Empty',
    description: 'If the value is empty, replace it with a custom default value',
    example: `"" → "N/A"`,
  },
  {
    type: 'DEFAULT_UNLESS_VALUE',
    label: 'Default Unless Value Equals',
    description:
      'If the value is NOT equal to a specific value, replace it with a default. Useful for Y/N fields.',
    example: `If not "0" → "N" (for SS exempt: any contribution means not exempt)`,
  },
  {
    type: 'CONVERT_BLANK_TO_EMPTY',
    label: 'Convert Blank-Like Values to Empty',
    description:
      'Converts placeholder values like "N/A", "00/00/0000", "-", or whitespace-only values to empty',
    example: `"N/A" → "", "00/00/0000" → ""`,
  },
  {
    type: 'PAD_STRING',
    label: 'Pad Value to Target Length',
    description:
      'Pads the start or end of a value with a character until it reaches a target length',
    example: `SSN "1234" padded with "0" to length 9 → "000001234"`,
  },
  {
    type: 'SUBSTRING',
    label: 'Extract Portion of Value',
    description:
      'Extract a portion of the value by position. Use positive numbers to count from start (1 = first), negative from end (-1 = last).',
    example: `"123-45-6789" start at -4, length 4 → "6789" (last 4)`,
  },
  {
    type: 'SPLIT_AND_EXTRACT',
    label: 'Split by Delimiter and Extract',
    description:
      'Split the value by a delimiter (comma, space, etc.) and extract a specific part. Use positive numbers to count from start (1 = first part), negative from end (-1 = last part).',
    example: `"Doe, John" split by ", " extract part 2 → "John"`,
  },
  {
    type: 'TRIM',
    label: 'Remove Whitespace',
    description: 'Removes all spaces',
    example: `" John Doe " → "JohnDoe"`,
  },
  {
    type: 'TO_UPPERCASE',
    label: 'Convert to Uppercase',
    description: 'Converts all letters to uppercase',
    example: `"John Doe" → "JOHN DOE"`,
  },
  {
    type: 'TO_LOWERCASE',
    label: 'Convert to Lowercase',
    description: 'Converts all letters to lowercase',
    example: `"John Doe" → "john doe"`,
  },
  {
    type: 'REMOVE_NON_NUMERIC',
    label: 'Remove Non-Numeric Characters',
    description: 'Keeps only numeric characters (0-9)',
    example: `"$1,234.56" → "123456"`,
  },
  {
    type: 'REMOVE_NON_ALPHABETIC',
    label: 'Remove Non-Alphabetic Characters',
    description: 'Keeps only letters (a-z, A-Z)',
    example: `"John123 Doe!" → "JohnDoe"`,
  },
  {
    type: 'PARSE_NUMBER',
    label: 'Parse as Number',
    description: 'Converts the value to a numeric format',
    example: `"$1,234.56" → "1234.56"`,
  },
];

export const MINIMUM_MAPPING_SUGGESTION_SCORE = 50;
